import {
  Component,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  Input,
  ViewChild,
} from "@angular/core";
import { map, Observable, startWith, Subscription } from "rxjs";
import {
  PredicateData,
  Rule,
  ConditionGroup,
  NestedGroups,
  RULE_VALUE_CHIP_PLACEHOLDER,
  RULE_VALUE_MAX_COUNT,
} from "../models/rule-engine";
import { FormBuilder, FormGroup } from "@angular/forms";
import { DataParserService } from "../../services/data-parser.service";
import { FormControl, Validators } from "@angular/forms";
import {
  getRuleNameValidationMessage,
  isDuplicateRuleName,
  isRuleReferencedInExpression,
  RULE_NAME_ALREADY_EXISTS_MESSAGE,
  ruleNameFormatValidator,
  RuleEngineService,
} from "../../services/rule-engine.service";
declare const copyToClipboard: any;
import { ToastrService } from "ngx-toastr";
import {
  MatSelect,
  MatSelectChange,
  MAT_SELECT_SCROLL_STRATEGY,
} from "@angular/material/select";
import { Overlay, ScrollStrategy } from "@angular/cdk/overlay";
import { MatDialog } from "@angular/material/dialog";
import { CustomRulesComponent } from "../custom-rules/custom-rules.component";
import { ConfirmDialogComponent, ConfirmDialogModel } from "src/app/confirm-dialog/confirm-dialog.component";
/** ADDED: Rule Child Operator mat-select — reposition instead of app-wide block() to avoid screen freeze inside Rule Builder dialog. */
export function createRulesMatSelectScrollStrategyFactory(
  overlay: Overlay
): () => ScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

@Component({
  selector: "app-create-rules",
  templateUrl: "./create-rules.component.html",
  styleUrls: ["./create-rules.component.scss"],
  providers: [
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: createRulesMatSelectScrollStrategyFactory,
    },
  ],
})
export class CreateRulesComponent implements OnInit, OnDestroy {
  readonly ruleValueChipPlaceholder = RULE_VALUE_CHIP_PLACEHOLDER;
  readonly ruleValueMaxCount = RULE_VALUE_MAX_COUNT;

  private static readonly OPERATOR_SELECT_BODY_CLASS =
    "create-rules-operator-select-open";
  private static readonly OPERATOR_SELECT_PANEL_CLASS =
    "create-rules-operator-select-panel";

  @ViewChild("operatorSelectRef") operatorSelectRef!: MatSelect;
  private operatorSelectOutsideClickListener: ((event: Event) => void) | null =
    null;

  @Output() onClickOpenTemplate = new EventEmitter<any>();
  field? = new FormControl("", Validators.required);
  userValue? = new FormControl("", Validators.required);
  /** Chip input for IN / NOT IN multi-value rules. */
  ruleValueChipInput = new FormControl("");
  ruleValueChips: string[] = [];
  ruleName? = new FormControl("", [
    Validators.required,
    ruleNameFormatValidator,
  ]);
  selectedOperator = new FormControl("", Validators.required);
  rules: Rule[] = [];
  ruleNumber: number = 0;
  isPopUp: boolean = false;
  isEdit: boolean = false;
  nodePaths: string[] = [];
  defaultLogicalOperatorForSimpleFilter: string = "AND";
  finalRuleExpression: string;
  editingRuleNumber: number | null = null;
  @Input() isTemplateTreeOpen: boolean = false;
  @Input() deliveryTemplateKey: string = 'DEFAULT';
  @Input() routeUniqueID: any;
  private _nodesWithTheirLeafNodesSubscription!: Subscription;
  private _pathSubscription!: Subscription;
  private _dataParserService!: DataParserService;
  private _nodePathsSubscription!: Subscription;
  private _finalExpressionSubscription!: Subscription;
  private _ruleEngineService!: RuleEngineService;
  private _updateFieldWithPredicateConditionsSubscription!: Subscription;
  private _rulesSubjectSubscription!: Subscription;
  nodesWithLeafNodes: any;
  validSubFieldsToSetFilter: any[] = [];

  filteredOptions!: Observable<string[]>;

  constructor(
    dataParserService: DataParserService,
    readonly ruleEngineService: RuleEngineService,
    private toastr: ToastrService,
    private _fb: FormBuilder,
    public dialog: MatDialog
  ) {
    this._dataParserService = dataParserService;
    this._ruleEngineService = ruleEngineService;
  }
  ngOnInit(): void {
    // Clear any prior interface template state before subscriptions receive BehaviorSubject values.
    this._dataParserService.resetTemplateParserState();

    this._pathSubscription = this._ruleEngineService.getPath().subscribe({
      next: (pathAsArrayOfString) =>
        this.field.setValue(pathAsArrayOfString?.join("/")),
      error: (err) => {
        this.toastr.error("Unable to get xpaths", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      },
    });
    this._nodePathsSubscription =
      this._dataParserService.$getNodePaths.subscribe({
        next: (nodePaths) => {
          this.nodePaths = nodePaths || [];
        },
        error: (err) => {
          this.toastr.error("Unable to get node paths", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
    this.filteredOptions = this.field.valueChanges.pipe(
      startWith(""),
      map((value) => this._filter(value || ""))
    );
    this._nodesWithTheirLeafNodesSubscription =
      this._dataParserService.$getNodesWithTheirLeafNodes.subscribe({
        next: (data) => (this.nodesWithLeafNodes = data),
        error: (err) => {
          this.toastr.error("Unable to get leaf nodes", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
    this._updateFieldWithPredicateConditionsSubscription =
      this._ruleEngineService.$getUpdatedFieldWithPredicate.subscribe({
        next: (data) => this.field.setValue(data),
        error: (err) => {
          this.toastr.error("Unable to get field", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
    this._rulesSubjectSubscription = this._ruleEngineService
      .getAllRules()
      .subscribe({
        next: (data) => (this.rules = data ?? []),
        error: (err) => {
          this.toastr.error("Unable to get rules", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
    this._finalExpressionSubscription =
      this._ruleEngineService.$getFinalRuleExpression.subscribe({
        next: (finalRuleExpression) =>
          (this.finalRuleExpression = finalRuleExpression),
        error: (err) => {
          this.toastr.error("Unable to get final rule expression", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
    this._dataParserService.getJsonDataForMatTree();
    // this._dataParserService.loadAPData();
    // this._dataParserService.loadCPData();
    // Prevent stale template reuse between interfaces:
    // Result Outbound must initialize with HL7 template, while others keep existing XML flow.
    if ((this.deliveryTemplateKey || '').toUpperCase() === 'RESULT_OUTBOUND') {
      this._dataParserService.loadResultOutboundData();
    } else {
      this._dataParserService.loadAPData();
      this._dataParserService.loadCPData();
    }
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    if (!this.nodePaths) {
      return []; // Return an empty array if nodePaths is invalid
    }
    return this.nodePaths.filter((option) =>
      option.toLowerCase().includes(filterValue)
    );
  }
  ngOnDestroy(): void {
    if (this._pathSubscription) {
      this._pathSubscription.unsubscribe();
    }
    if (this._nodePathsSubscription) {
      this._nodePathsSubscription.unsubscribe();
    }
    if (this._nodesWithTheirLeafNodesSubscription) {
      this._nodesWithTheirLeafNodesSubscription.unsubscribe();
    }
    if (this._updateFieldWithPredicateConditionsSubscription) {
      this._updateFieldWithPredicateConditionsSubscription.unsubscribe();
    }
    if (this._finalExpressionSubscription) {
      this._finalExpressionSubscription.unsubscribe();
    }
    if (this._rulesSubjectSubscription) {
      this._rulesSubjectSubscription.unsubscribe();
    }
    this.nodePaths = [];
    this.filteredOptions = new Observable((observer) => observer.next([]));
    this._ruleEngineService.clearXpath = true;
    this._ruleEngineService.setFieldWithUpdatedPredicate("");
    this._ruleEngineService.setSubConditionsFor("", "", "");
    this.clearAll();
    this.cleanupOperatorSelectOverlayState();
    this.cleanupFieldAutocompleteScrollState();
  }

  private getRuleNameInputValue(): string {
    const raw = this.ruleName.disabled
      ? this.ruleName.getRawValue()
      : this.ruleName.value;
    return (raw ?? "").toString().trim();
  }

  get isButtonDisabled() {
    return (
      !this.field.value ||
      !this.hasValidRuleValue() ||
      !this.getRuleNameInputValue() ||
      !this.selectedOperator.value
    );
  }

  onRuleNameBlur(): void {
    this.ruleName.markAsTouched();
    this.showRuleNameValidationToastIfNeeded();
  }

  private showRuleNameValidationToastIfNeeded(): boolean {
    const message = getRuleNameValidationMessage(this.getRuleNameInputValue());
    if (!message) {
      return false;
    }
    this.toastr.warning(message, "", {
      timeOut: 4000,
      positionClass: "toast-bottom-right",
    });
    return true;
  }

  get useRuleValueChips(): boolean {
    return this._ruleEngineService.isMultiValueOperator(
      this.selectedOperator.value
    );
  }

  /** ADDED: validates chip list (IN/NOT IN) or legacy text value (other operators). */
  private hasValidRuleValue(): boolean {
    if (this.useRuleValueChips) {
      const pending = (this.ruleValueChipInput.value ?? "").toString().trim();
      return this.ruleValueChips.length > 0 || !!pending;
    }
    return !!(this.userValue.value ?? "").toString().trim();
  }

  /** ADDED: resolves stored pipe-delimited value from chips or text input. */
  private getRuleValueForSave(): string {
    if (this.useRuleValueChips) {
      return this._ruleEngineService.joinRuleValues(this.ruleValueChips);
    }
    // COMMENTED OUT path: raw userValue with pipe normalization only (non chip operators).
    return this._ruleEngineService.normalizeRuleValue(this.userValue.value);
  }

  /** ADDED: grid cell — first value + (+N) for multi-value rules. */
  getRuleValueDisplay(value: string | undefined): ReturnType<
    RuleEngineService["formatRuleValueDisplay"]
  > {
    return this._ruleEngineService.formatRuleValueDisplay(value);
  }

  /** ADDED: load chip list when editing or switching to IN/NOT IN. */
  private loadRuleValueChipsFromValue(value: string | undefined): void {
    this.ruleValueChips = [...this._ruleEngineService.parseRuleValues(value)];
    this.ruleValueChipInput.reset();
    this.syncUserValueFromChips();
  }

  /** ADDED: keep userValue in sync so legacy validation paths still work. */
  private syncUserValueFromChips(): void {
    const joined = this._ruleEngineService.joinRuleValues(this.ruleValueChips);
    this.userValue.setValue(joined, { emitEvent: false });
  }

  /** ADDED: clear chip state. */
  private clearRuleValueChips(): void {
    this.ruleValueChips = [];
    this.ruleValueChipInput.reset();
  }

  /** ADDED: add value via + Add button or Enter in the Add Value input. */
  onAddRuleValueFromInput(): void {
    const raw = (this.ruleValueChipInput.value ?? "").toString().trim();
    if (!raw) {
      return;
    }
    const result = this._ruleEngineService.appendRuleValuesFromInput(
      this.ruleValueChips,
      raw,
      this.ruleValueMaxCount
    );
    if (result.limitReached) {
      this.toastr.warning(
        `Maximum ${this.ruleValueMaxCount} values allowed`,
        "",
        { timeOut: 4000, positionClass: "toast-bottom-right" }
      );
    }
    this.ruleValueChips = [...result.values];
    this.ruleValueChipInput.reset();
    this.syncUserValueFromChips();
  }

  /** ADDED: remove a single chip value. */
  removeRuleValueChip(chip: string): void {
    const index = this.ruleValueChips.indexOf(chip);
    if (index >= 0) {
      this.ruleValueChips.splice(index, 1);
      this.syncUserValueFromChips();
    }
  }

  /** Flush typed text into chips before save — first value is always stored as a chip. */
  commitPendingRuleValueChipInput(): void {
    if (!this.useRuleValueChips) {
      return;
    }
    const raw = (this.ruleValueChipInput.value ?? "").toString().trim();
    if (!raw) {
      return;
    }
    this.onAddRuleValueFromInput();
  }

  onClickAddOrEdit() {
    this.commitPendingRuleValueChipInput();
    this.ruleName.markAsTouched();
    if (this.showRuleNameValidationToastIfNeeded()) {
      return;
    }
    if (this.isButtonDisabled) {
      return;
    }
    if (!this.isEdit) {
      if (!this.checkDuplicateRuleName()) {
        this.addRuleToTheArray();
        this.clearAllTheValues();
      }
    } else {
      if (!this.checkEditDupliate()) {
        this.addEditedRuleToTheArray();
        this.resetEditingState();
        this.clearAllTheValues();
      } else {
        // Reset editing state even if validation fails
        this.resetEditingState();
      }
    }
    this._ruleEngineService.setAllTheRules(this.rules);
    this._ruleEngineService.setSubConditionsFor("", "", "");
  }
  checkDuplicateRuleName(): boolean {
    if (isDuplicateRuleName(this.rules, this.getRuleNameInputValue())) {
      this.showDuplicateRuleNameWarning();
      return true;
    }
    return false;
  }
  checkEditDupliate(): boolean {
    if (
      isDuplicateRuleName(this.rules, this.getRuleNameInputValue(), {
        excludeRuleNumber: this.ruleNumber,
      })
    ) {
      this.showDuplicateRuleNameWarning();
      return true;
    }
    return false;
  }
  private showDuplicateRuleNameWarning(): void {
    this.toastr.warning(RULE_NAME_ALREADY_EXISTS_MESSAGE, "", {
      timeOut: 4000,
      positionClass: "toast-bottom-right",
    });
  }
  addRuleToTheArray() {
    this.rules?.push({
      rulenumber: this.rules.length + 1,
      rulename: this.getRuleNameInputValue(),
      // value: this.userValue.value.replace(/\s*\|\s*/g, '|').trim(),
      value: this.getRuleValueForSave(),
      field: this.field.value,
      operator: this.selectedOperator.value,
    });
  }
  clearAllTheValues() {
    this.ruleNumber = 0;
    this.field.reset();
    this.userValue.reset();
    this.clearRuleValueChips();
    this.ruleName.reset();
    this.selectedOperator.reset();
    this.editingRuleNumber = null; // Ensure editing state is reset
  }

  clearOperatorOnly() {
    // Clear only the operator field, keep all other values
    this.selectedOperator.reset();
  }

  async deleteASingleRuleFromRulesArray(ruleNumber: number) {
    let confirmDelete = await this.confirmDeleteDialogue();
    if (confirmDelete) {
        let filteredRules = this.rules.filter(
            (rule: Rule) => rule.rulenumber !== ruleNumber
        );
        filteredRules.forEach((rule: Rule, index: number) => {
            rule.rulenumber = index + 1;
        });
        //this.checkAndResetTheFinalRuleExpression(ruleNumber);
        this.rules = filteredRules;
        this._ruleEngineService.setAllTheRules(this.rules);
    }
}


  async confirmDeleteDialogue(){
    const message = `Are you sure you want to delete this Rule?`;
    const dialogData = new ConfirmDialogModel("Confirm Action", message, 'confirm', '');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { maxWidth: "500px", data: dialogData, disableClose: true });
    const dialogResult = await dialogRef.afterClosed().toPromise();
    return dialogResult;
  }
  checkAndResetTheFinalRuleExpression(ruleNumber: number) {
    let ruleToDelete = this.rules.find((rule: Rule) => {
      if (rule.rulenumber === ruleNumber) {
        return rule;
      }
    });
    if (
      ruleToDelete !== null ||
      ruleToDelete !== undefined ||
      ruleToDelete.rulename !== ""
    ) {
      let ruleNames = this.getTheRuleNamesFromFinalExpression();
      let isRulePresent = ruleNames.some(
        (ruleName: any) => ruleName === ruleToDelete.rulename
      );
      if (isRulePresent) {
        this._ruleEngineService.setFinalRuleExpression("");
        this.prepareTheJsonForEmptyFinalRuleExpression();
      }
    }
  }
  //#region This method prepares the JSON needed for showing the contents of the expression builder when the final rule expression is empty
  prepareTheJsonForEmptyFinalRuleExpression() {
    let expressionGroupObj = {};
    let expressionGroupArray = [];
    let expressionArray = [];
    let expressionGroup = {
      condition: "",
      rule: "",
    };
    expressionArray.push(expressionGroup);
    let expression = {
      condition: "",
      rule: "",
    };
    expressionArray.push(expression);
    expressionGroupArray.push({
      expressionArray: expressionArray,
      groupOperator: "",
    });
    expressionGroupObj = {
      expressionGroupArray: expressionGroupArray,
    };
    this._ruleEngineService.holdTheBuildExpressionData = expressionGroupObj;
  }
  //#endregion

  getTheRuleNamesFromFinalExpression() {
    let ruleNamesToReturn = [];
    const regexSingleRule =
      /^\(\s*[a-zA-Z0-9_]+(?:\s+(?!AND|OR\b)[a-zA-Z0-9_]+)*\s*\)$/i;
    const regexAndOrRule =
      /^\(\s*[a-zA-Z0-9_ ]+(\s+(AND|OR)\s+[a-zA-Z0-9_ ]+)+\s*\)$/i;
    const regexMultipleAndOr =
      /^\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)(\s*(AND|OR)\s*\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\))*$/i;
    if (regexSingleRule.test(this.finalRuleExpression.trim())) {
      const ruleName = this.finalRuleExpression.replace(/[()]/g, "").trim();
      ruleNamesToReturn.push(ruleName);
    } else if (regexAndOrRule.test(this.finalRuleExpression.trim())) {
      const innerExpression = this.finalRuleExpression.trim().slice(1, -1);
      const ruleNames = innerExpression.split(/\s+(AND|OR)\s+/i);
      ruleNamesToReturn.push(...ruleNames);
    } else if (regexMultipleAndOr.test(this.finalRuleExpression.trim())) {
      const regexGroup =
        /\(\s*[a-zA-Z0-9_ ]+(\s*(AND|OR)\s*[a-zA-Z0-9_ ]+)?\s*\)/gi;
      const groups = this.finalRuleExpression.match(regexGroup) || [];
      groups.map((group: any) => {
        const innerExpression = group.trim().slice(1, -1);
        const ruleNames = innerExpression.split(/\s+(AND|OR)\s+/i);
        ruleNamesToReturn.push(...ruleNames);
      });
    }
    return ruleNamesToReturn;
  }
  editASingleRuleFromRulesArray(rule: Rule) {
    if (rule.ruletype?.toLowerCase() === "method") {
      this.OpenCustomRulesComponent(rule);
    } else {
      this.editingRuleNumber = rule.rulenumber;
      this.isEdit = true;
      this.field.setValue(rule.field);
      // this.userValue.setValue(rule.value);
      this.ruleName.setValue(rule.rulename);
      this.ruleNumber = rule.rulenumber;
      this.selectedOperator.setValue(rule.operator);
      if (this._ruleEngineService.isMultiValueOperator(rule.operator)) {
        this.loadRuleValueChipsFromValue(rule.value);
      } else {
        this.clearRuleValueChips();
        this.userValue.setValue(rule.value);
      }
      if (
        this.isRuleUsedInFinalExpression(
          rule.rulename,
          this.finalRuleExpression,
          rule.rulenumber
        )
      ) {
        this.ruleName.disable();
      }
    }
  }

  addEditedRuleToTheArray() {
    this.rules.map((rule: any) => {
      if (rule.rulenumber === this.ruleNumber) {
        rule.rulename = this.getRuleNameInputValue();
        rule.field = this.field.value;
        // rule.value = this.userValue.value.replace(/\s*\|\s*/g, '|').trim();
        rule.value = this.getRuleValueForSave();
        rule.operator = this.selectedOperator.value;
      }
    });
  }

  isRuleUsedInFinalExpression(
    ruleName: string,
    finalExpression: string,
    ruleNumber?: number
  ): boolean {
    return isRuleReferencedInExpression(
      { rulename: ruleName, rulenumber: ruleNumber },
      finalExpression
    );
  }

  //clears the form controls as soon as we close the rule engine
  clearAll() {
    this.ruleName.reset();
    this.field.reset();
    this.userValue.reset();
    this.clearRuleValueChips();
    this.selectedOperator.reset();
  }
  clearTheValues() {
    this.ruleName.reset();
    if (!this.isEdit) {
      this.ruleNumber = 0;
    }
    this.field.reset();
    this.userValue.reset();
    this.clearRuleValueChips();
    this.selectedOperator.reset();
    this.resetEditingState();
  }

  // Helper method to reset editing state
  private resetEditingState() {
    this.editingRuleNumber = null;
    this.isEdit = false;
    this.ruleName.enable();
  }
  onDoubleClickRuleName() {
    // Check if the path contains an attribute (@) - only open modal if it does
    const fieldValue = this.field.value;
    if (!fieldValue?.includes("@")) {
      // Path does not contain an attribute, so don't open the modal
      this.isPopUp = false;
      this._ruleEngineService.setPopUpValue(this.isPopUp);
      return;
    }

    //this will split the path in the filed form control based on the '/'
    let individualNodes = fieldValue.split("/");
    //we are storing the last part of the xpath from the user entered field as we need it later to check if the xpath actually exists in the list of all the xpaths we have got from the xml template
    let lastPartOfTheField = individualNodes[individualNodes.length - 1];
    //here will get all the nodes and their filters as key and values
    let kvpOfNodeAndFilters: { [key: string]: string | null }[] =
      this.getKeyValueOfNodeAndFilter(individualNodes);
    //here we will check if the xpath after removing all the filters will match with any of the xpaths that we have generated from the xml template
    let allowEdit = this.isPathMatch(kvpOfNodeAndFilters, lastPartOfTheField);
    if (!allowEdit) {
      this.toastr.warning("The path is not present in AP or CP", "", {
        timeOut: 4000,
        positionClass: "toast-bottom-right",
      });

      //here we are trying to make sure that the predicate filter does not open because there is no matching path in the CP or AP
      this.isPopUp = false;
      this._ruleEngineService.setPopUpValue(this.isPopUp);
    } else {
      let xpathWithoutFilters =
        this.getXPathWithoutFilters(kvpOfNodeAndFilters);
      let xPaths = this.generateXPathsFromField(xpathWithoutFilters);
      let listOfPredicateData = this.getNodesWithLeaves(xPaths);
      let hasFilters = this.hasFilters(kvpOfNodeAndFilters);
      if (hasFilters) {
        let finalPredicateData = this.generateTheDataWhenFilters(
          kvpOfNodeAndFilters,
          listOfPredicateData
        );
        this._ruleEngineService.dataToSendForPredicatePopUp =
          finalPredicateData;
        this._ruleEngineService.setSubConditionsFor(
          xpathWithoutFilters,
          this.nodesWithLeafNodes,
          this.nodePaths
        );
      } else {
        let finalPredicateData =
          this.generateTheDataWhenNoFilters(listOfPredicateData);
        this._ruleEngineService.dataToSendForPredicatePopUp =
          finalPredicateData;
        this._ruleEngineService.setSubConditionsFor(
          xpathWithoutFilters,
          this.nodesWithLeafNodes,
          this.nodePaths
        );
      }

      //here we are trying to make sure that the predicate filter does open because there is matching path in the CP or AP
      this.isPopUp = true;
      this._ruleEngineService.setPopUpValue(this.isPopUp);
    }
  }

  //here we are adding the form group properly based on the filters present
  generateTheDataWhenFilters(
    kvpOfNodeAndFilters: { [key: string]: string | null }[],
    listOfPredicateData: PredicateData[]
  ): PredicateData[] {
    let isSimple: boolean = false;
    let isGroup: boolean = false;
    let isNested: boolean = false;
    let predicateDataWithFormData: PredicateData[] = [];
    let attributes: any[] = [];
    kvpOfNodeAndFilters.forEach((kvp: { [key: string]: string | null }) => {
      const key = Object.keys(kvp)[0];
      const value = kvp[key];
      listOfPredicateData.forEach((predicateData: PredicateData) => {
        if (key === predicateData.nodeName && value !== null && value !== "") {
          isSimple = this.checkForSimpleFilter(value);
          isGroup = this.checkForGroupFilter(value);
          isNested = this.checkForNestedFilter(value);
          let matchingNodes = this.nodesWithLeafNodes.filter(
            (node: any) => node.nodeName === predicateData.nodePath
          );
          matchingNodes?.length > 0
            ? (attributes = matchingNodes[0].leafNodes)
            : (attributes = []);

          if (isSimple) {
            let formData = this.generateFormDataForSimpleFilter(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          } else if (isGroup) {
            let formData = this.generateFormDataForGroup(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          } else if (isNested) {
            let formData = this.generateFormDataForNestedFilter(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          }
        } else if (
          key === predicateData.nodeName &&
          (value === null || value === "")
        ) {
          let predicateWithFormData: PredicateData = {
            nodeName: predicateData.nodeName,
            nodePath: predicateData.nodePath,
            attributes: predicateData.attributes,
            formGroup: this._fb.group({
              groupArray: this._fb.array([]),
            }),
          };
          predicateDataWithFormData.push(predicateWithFormData);
        }
      });
    });
    return predicateDataWithFormData;
  }

  generateFormDataForSimpleFilter(
    filterValue: string,
    logicalOperator = this.defaultLogicalOperatorForSimpleFilter
  ): FormGroup {
    const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    const values = filterValue.match(regex);
    const condition = values[2];
    const value = values[3];
    const attribute = values[1];
    if (values) {
      let formGroup = this._fb.group({
        groupArray: this._fb.array([
          this._fb.group({
            logicalOperator: this._fb.control(
              logicalOperator,
              Validators.required
            ),
            conditions: this._fb.array([
              this.createConditionGroup(attribute, condition, value),
            ]),
          }),
        ]),
      });
      return formGroup;
    }
  }
  createConditionGroup(
    attribute: string,
    condition: string,
    value: string
  ): FormGroup {
    return this._fb.group({
      attribute: this._fb.control(attribute, Validators.required),
      condition: this._fb.control(condition, Validators.required),
      value: this._fb.control(value, Validators.required),
    });
  }
  checkForSimpleFilter(filterValue: string): boolean {
    let regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    return regex.test(filterValue);
  }
  checkForGroupFilter(filterValue: string): boolean {
    let regex =
      /^\[\s*\(\s*@\w+\s*[=!<>~]{1,3}\s*'[^']+'\s*(\s*(AND|OR)\s*@\w+\s*[=!<>~]{1,3}\s*'[^']+'\s*)+\)\s*\]$/;
    return regex.test(filterValue);
  }
  checkForNestedFilter(filterValue: string): boolean {
    let regex =
      /^\[\s*\(.*?\)\s+(AND|OR)\s+\(.*?\)(\s+(AND|OR)\s+\(.*?\))*\s*\]$/;
    return regex.test(filterValue);
  }

  generateFormDataForGroup(filterValue: string): FormGroup {
    const conditionsAndOperators =
      this.extractConditionsAndOperator(filterValue);
    const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    const multipleFormGroups: FormGroup[] = [];
    if (
      conditionsAndOperators.conditions.length > 1 &&
      (conditionsAndOperators.operator !== null ||
        conditionsAndOperators.operator !== "")
    ) {
      conditionsAndOperators.conditions.forEach((item: string) => {
        const values = item.match(regex);
        const condition = values[2];
        const value = values[3];
        const attribute = values[1];
        let singleFormGroup = this.createConditionGroup(
          attribute,
          condition,
          value
        );
        multipleFormGroups.push(singleFormGroup);
      });
    }

    let formGroup = this._fb.group({
      groupArray: this._fb.array([
        this._fb.group({
          logicalOperator: this._fb.control(
            conditionsAndOperators.operator,
            Validators.required
          ),
          conditions: this._fb.array(multipleFormGroups),
        }),
      ]),
    });
    return formGroup;
  }

  generateFormDataForNestedFilter(filterValue: string): FormGroup {
    //first we want all the groups and operators separated from the nested string, here we will get all the nested groups as array of strings and also operators as array of strings returned by the "extractNestedConditions()" method.
    let groupsAndCondition = this.extractNestedConditions(filterValue);
    const multipleOuterFormGroups: FormGroup[] = [];

    groupsAndCondition.groups.forEach((group: string) => {
      const multipleInnerFormGropus: FormGroup[] = [];
      const conditionsAndOperators = this.extractConditionsAndOperator(group);
      const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
      // here in this below if condition we are checking whether if the incoming conditions has just one or more than one conditons. if the incoming group has more than one condition like "(@Services!='no' AND @Status='valid')" then it will have operator but if it has just one condition like "(@orderno='654321')" then it will not have any operator so we will add the default operator by ourself and that is what is done here.
      if (
        conditionsAndOperators.operator === null ||
        conditionsAndOperators.operator === ""
      ) {
        conditionsAndOperators.operator =
          this.defaultLogicalOperatorForSimpleFilter;
      }

      conditionsAndOperators.conditions.forEach((item: string) => {
        const values = item.match(regex);
        const condition = values[2];
        const value = values[3];
        const attribute = values[1];
        let singleFormGroup = this.createConditionGroup(
          attribute,
          condition,
          value
        );
        multipleInnerFormGropus.push(singleFormGroup);
      });

      let formGroup = this._fb.group({
        logicalOperator: this._fb.control(
          conditionsAndOperators.operator,
          Validators.required
        ),
        conditions: this._fb.array(multipleInnerFormGropus),
      });
      multipleOuterFormGroups.push(formGroup);
    });

    let formGroup = this._fb.group({
      groupArray: this._fb.array(multipleOuterFormGroups),
    });
    return formGroup;
  }

  extractNestedConditions(filterValue: string) {
    let nestedGroups: NestedGroups;
    // step 1 - remove the '[' and ']'
    const trimmed = filterValue.trim().slice(1, -1).trim();

    // step 2 - match all the groups with '(' and ')' and extract them
    const groups = trimmed.match(/\(.*?\)/g);

    // step 3 - extracting all the group operators in the same order as they are present
    const operators = trimmed
      .split(/\(.*?\)/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    nestedGroups = { groups: groups, operators: operators };

    return nestedGroups;
  }
  //here we are extracting the operator and values
  extractConditionsAndOperator(filterValue: string): ConditionGroup {
    let conditionsAndOperators: ConditionGroup;
    // Step 1: Remove brackets
    const cleaned = filterValue.replace(/[\[\]\(\)]/g, "");

    // Step 2: Extract the operator (first match is enough, since they're all same)
    const operatorMatch = cleaned.match(/\b(AND|OR)\b/);
    const operator = operatorMatch ? operatorMatch[0] : null;

    // Step 3: Split the conditions by that operator
    const rawConditions = operator
      ? cleaned.split(new RegExp(`\\s+${operator}\\s+`))
      : [cleaned]; // if no operator found, return the full condition as a single entry

    //we adding the square braces [] to each of the filters
    const conditions = rawConditions.map((cond) => `[${cond.trim()}]`);
    conditionsAndOperators = { conditions: conditions, operator: operator };
    return conditionsAndOperators;
  }

  //here we are just adding the empty form group as the filters are not there and then we will return predicate data
  generateTheDataWhenNoFilters(
    listOfPredicateData: PredicateData[]
  ): PredicateData[] {
    return listOfPredicateData.map((item: any) => ({
      nodeName: item.nodeName,
      nodePath: item.nodePath,
      attributes: item.attributes,
      formGroup: this._fb.group({
        groupArray: this._fb.array([]),
      }),
    }));
  }
  hasFilters(kvpOfNodeAndFilters: { [key: string]: string | null }[]): boolean {
    let hasFilters = kvpOfNodeAndFilters.some((item: any) => {
      return Object.values(item).some(
        (value: any) => value !== null && value !== ""
      );
    });
    return hasFilters;
  }
  getNodesWithLeaves(xPaths: string[]): PredicateData[] {
    let predicatData: PredicateData;
    if (!Array.isArray(xPaths) || !Array.isArray(this.nodesWithLeafNodes)) {
      return []; // Return an empty array if inputs are invalid
    }

    // Filter `allTheNodes` where `nodeName` exists in `generatedPaths`
    const predicateData = this.nodesWithLeafNodes
      .filter((node) => xPaths.includes(node.nodeName)) // Filter matching paths
      .map((node) => {
        const parts = node.nodeName.split("/"); // Split the path by '/'
        const tabName = parts[parts.length - 1]; // Get the last segment of the path
        return (predicatData = {
          nodeName: tabName, // Use the last part as the nodeName
          attributes: node.leafNodes, // Keep the original leafNodes
          nodePath: node.nodeName, // Include the full path as nodePath
        });
      });
    return predicateData;
  }

  //this will generate multiple xpaths from the xpath provided by the user.
  //Eg: Case/OrderingProvider/Location[@AccountType='Ordering Facility']/@OrganizationGUID
  //Output will be array of strings of these values: "Case", "Case/OrderingProvider", "Case/OrderingProvider/Location"
  generateXPathsFromField(xPathsWithoutFilters: string): string[] {
    let xPathWithOnlyParentNodes = xPathsWithoutFilters
      ?.split("/")
      .slice(0, -1);
    if (
      !xPathWithOnlyParentNodes ||
      !Array.isArray(xPathWithOnlyParentNodes) ||
      xPathWithOnlyParentNodes.length === 0
    ) {
      return []; // Return an empty array if input is invalid
    }

    let xPaths: string[] = [];
    let currentPath = "";

    for (const path of xPathWithOnlyParentNodes) {
      currentPath = currentPath ? `${currentPath}/${path}` : path;
      xPaths.push(currentPath);
    }
    return xPaths;
  }
  getXPathWithoutFilters(
    kvpOfNodeAndFilters: { [key: string]: string | null }[]
  ): string {
    let xpathWithoutFilters: string = "";
    kvpOfNodeAndFilters.map((node: any, index: number) => {
      xpathWithoutFilters += Object.keys(node)[0];
      if (index !== kvpOfNodeAndFilters.length - 1) {
        xpathWithoutFilters += "/";
      }
    });
    //here we are manually adding the last part of the xpath that we have extracted earlier to finally arrive at the xpath which we need to compare with the xpaths generated form the xml
    xpathWithoutFilters;
    return xpathWithoutFilters;
  }
  //this method checks if the given path matches with the xpaths we have generated from xml. returns true if it matches or false
  isPathMatch(
    kvpOfNodeAndFilters: { [key: string]: string | null }[],
    lastPartOfTheField: string
  ): boolean {
    //here we are generating the plain xpath without the filters to compare and them with the xpaths we have generated from the loaded xml
    let xpathWithoutFilters = this.getXPathWithoutFilters(kvpOfNodeAndFilters);
    if (this.nodePaths.includes(xpathWithoutFilters)) {
      return true;
    } else {
      return false;
    }
  }

  //this method will take user entered xpath and return all the nodes as key and all the filters as their values
  getKeyValueOfNodeAndFilter(
    individualNodes: string[]
  ): { [key: string]: string | null }[] {
    let kvpOfNodeAndFilters: { [key: string]: string | null }[] = [];
    individualNodes.map((node: any) => {
      let regex = /([^\[]+)(\[[^\]]+\])?/;
      //first checking if the node has any filters by using the regular expression then returning true or false based on that
      let isMatch = regex.test(node);
      //take the first node add the node as key and its filters as value to the kvpOfNodeAndFilters variable
      if (isMatch) {
        const match = node.match(regex);
        if (match[2]) {
          let key = match[1];
          let value = match[2];
          kvpOfNodeAndFilters.push({ [key]: value });
        } else {
          let key = match[1];
          let value = null;
          kvpOfNodeAndFilters.push({ [key]: value });
        }
      }
    });
    return kvpOfNodeAndFilters;
  }

  // private filterNodesWithLeavesOnly(
  //   generatedPaths: string[],
  //   allTheNodes: { nodeName: string; leafNodes: string[] }[]
  // ): { nodeName: string; leafNodes: string[]; nodePath: string }[] {
  //   if (!Array.isArray(generatedPaths) || !Array.isArray(allTheNodes)) {
  //     return []; // Return an empty array if inputs are invalid
  //   }

  //   // Filter `allTheNodes` where `nodeName` exists in `generatedPaths`
  //   const result = allTheNodes
  //     .filter((node) => generatedPaths.includes(node.nodeName)) // Filter matching paths
  //     .map((node) => {
  //       const parts = node.nodeName.split("/"); // Split the path by '/'
  //       const lastPart = parts[parts.length - 1]; // Get the last segment of the path

  //       return {
  //         nodeName: lastPart, // Use the last part as the nodeName
  //         leafNodes: node.leafNodes, // Keep the original leafNodes
  //         nodePath: node.nodeName, // Include the full path as nodePath
  //       };
  //     });

  //   return result;
  // }

  // private generatePaths(paths: any): string[] {
  //   if (!paths || !Array.isArray(paths) || paths.length === 0) {
  //     return []; // Return an empty array if input is invalid
  //   }

  //   let result: string[] = [];
  //   let currentPath = "";

  //   for (const path of paths) {
  //     currentPath = currentPath ? `${currentPath}/${path}` : path;
  //     result.push(currentPath);
  //   }

  //   return result;
  // }

  copyToClip(event: any, message: string) {
    event != "" ? copyToClipboard(event) : "";
    this.toastr.success(message + " copied", "", {
      timeOut: 500,
      positionClass: "toast-bottom-right",
    });
  }
  onClickOpenTemplateBtn() {
    this.onClickOpenTemplate.emit(true);
    this.isTemplateTreeOpen = true;
  }
  onOperatorChange(event: MatSelectChange) {
    if (event.value.toLowerCase() === "custom") {
      this.OpenCustomRulesComponent();
      return;
    }
    if (this._ruleEngineService.isMultiValueOperator(event.value)) {
      const existing = this._ruleEngineService.normalizeRuleValue(
        this.userValue.value
      );
      this.loadRuleValueChipsFromValue(existing);
    } else {
      this.clearRuleValueChips();
    }
  }

  /** ADDED: toggle non-blocking backdrop + outside-click close for Operator mat-select. */
  onOperatorSelectOpenedChange(opened: boolean): void {
    if (opened) {
      document.body.classList.add(
        CreateRulesComponent.OPERATOR_SELECT_BODY_CLASS
      );
      this.attachOperatorSelectOutsideClickListener();
    } else {
      this.cleanupOperatorSelectOverlayState();
    }
  }

  private attachOperatorSelectOutsideClickListener(): void {
    this.detachOperatorSelectOutsideClickListener();
    this.operatorSelectOutsideClickListener = (event: Event) => {
      const target = event.target as Node | null;
      if (!target || !this.operatorSelectRef) {
        return;
      }
      const triggerEl = this.operatorSelectRef._elementRef
        ?.nativeElement as HTMLElement | undefined;
      if (triggerEl?.contains(target)) {
        return;
      }
      const panels = document.querySelectorAll(
        `.${CreateRulesComponent.OPERATOR_SELECT_PANEL_CLASS}`
      );
      for (let i = 0; i < panels.length; i++) {
        if (panels[i].contains(target)) {
          return;
        }
      }
      if (this.operatorSelectRef.panelOpen) {
        this.operatorSelectRef.close();
      }
    };
    setTimeout(() => {
      if (this.operatorSelectOutsideClickListener) {
        document.addEventListener(
          "click",
          this.operatorSelectOutsideClickListener,
          true
        );
        document.addEventListener(
          "touchend",
          this.operatorSelectOutsideClickListener,
          true
        );
      }
    }, 0);
  }

  private detachOperatorSelectOutsideClickListener(): void {
    if (this.operatorSelectOutsideClickListener) {
      document.removeEventListener(
        "click",
        this.operatorSelectOutsideClickListener,
        true
      );
      document.removeEventListener(
        "touchend",
        this.operatorSelectOutsideClickListener,
        true
      );
      this.operatorSelectOutsideClickListener = null;
    }
  }

  private cleanupOperatorSelectOverlayState(): void {
    document.body.classList.remove(
      CreateRulesComponent.OPERATOR_SELECT_BODY_CLASS
    );
    this.detachOperatorSelectOutsideClickListener();
  }

  // ADDED: Rule Field autocomplete scroll — Material 16 [class] on overlay + wheel bridge (Rule screen only).
  readonly fieldAutocompletePanelClass = "rule-field-autocomplete-scroll-panel";
  private fieldAutocompleteScrollWheelOpenCount = 0;
  private fieldAutocompleteScrollWheelHandler: ((e: WheelEvent) => void) | null =
    null;

  onFieldAutocompletePanelOpened(): void {
    this.fieldAutocompleteScrollWheelOpenCount++;
    if (this.fieldAutocompleteScrollWheelOpenCount !== 1) {
      return;
    }
    this.fieldAutocompleteScrollWheelHandler = (e: WheelEvent) => {
      const panel = document.querySelector(
        ".cdk-overlay-container .mat-mdc-autocomplete-panel.rule-field-autocomplete-scroll-panel"
      ) as HTMLElement | null;
      if (!panel) {
        return;
      }
      const r = panel.getBoundingClientRect();
      const { clientX: x, clientY: y } = e;
      if (x < r.left || x > r.right || y < r.top || y > r.bottom) {
        return;
      }
      const maxScroll = panel.scrollHeight - panel.clientHeight;
      if (maxScroll <= 0) {
        return;
      }
      const prev = panel.scrollTop;
      const next = Math.max(0, Math.min(maxScroll, prev + e.deltaY));
      if (
        next === prev &&
        ((e.deltaY > 0 && prev >= maxScroll - 0.5) ||
          (e.deltaY < 0 && prev <= 0.5))
      ) {
        return;
      }
      panel.scrollTop = next;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener("wheel", this.fieldAutocompleteScrollWheelHandler, {
      passive: false,
      capture: true,
    });
  }

  onFieldAutocompletePanelClosed(): void {
    this.fieldAutocompleteScrollWheelOpenCount = Math.max(
      0,
      this.fieldAutocompleteScrollWheelOpenCount - 1
    );
    if (this.fieldAutocompleteScrollWheelOpenCount > 0) {
      return;
    }
    this.detachFieldAutocompleteScrollWheelListener();
  }

  private detachFieldAutocompleteScrollWheelListener(): void {
    if (this.fieldAutocompleteScrollWheelHandler) {
      document.removeEventListener(
        "wheel",
        this.fieldAutocompleteScrollWheelHandler,
        { capture: true } as AddEventListenerOptions
      );
      this.fieldAutocompleteScrollWheelHandler = null;
    }
  }

  private cleanupFieldAutocompleteScrollState(): void {
    this.fieldAutocompleteScrollWheelOpenCount = 0;
    this.detachFieldAutocompleteScrollWheelListener();
  }

  private OpenCustomRulesComponent(rule?: Rule) {
    const dialogRef = this.dialog.open(CustomRulesComponent, {
      // EXISTING (commented): height auto caused unstable size; 18vw maxHeight was unreliable.
      // height: "auto",
      // maxHeight: "18vw !important",
      // ADDED: Fixed dialog size, slightly below top of viewport.
      height: "55%",
      width: "90%",
      maxHeight: "85vh",
      panelClass: "custom-rules-dialog",
      position: { top: "10vh" },
      data: {
        rule,
        finalRuleExpression: this.finalRuleExpression || "",
        routeUniqueID: this.routeUniqueID,
      },
      disableClose: true,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result === true) {
          // If custom rule was successfully saved, clear all values
          this.clearAllTheValues();
        } else {
          // If cancelled, clear only the operator field, keep everything else
          this.clearOperatorOnly();
        }
        this.resetEditingState();
      },
    });
  }
}